import { useEffect } from 'react';
import { Briefcase, Camera, CloudUpload, Flag } from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import { usePartnerManifest } from '@/hooks/usePartnerManifest';
import { applyFavicon } from '@/lib/favicon';
import { Badge } from '@/components/ui/badge';
import { DegradedNotice } from '@/components/capture/DegradedNotice';
import { activeSection, type NavSection } from './nav-section';

const NAV_ITEMS: { section: NavSection; to: string; label: string; icon: typeof Briefcase }[] = [
  { section: 'jobs', to: '/work', label: 'Jobs', icon: Briefcase },
  // `/capture` opens the chosen job's viewfinder, or My jobs when no job
  // has been chosen (`routes/CaptureEntry.tsx`).
  { section: 'capture', to: '/capture', label: 'Capture', icon: Camera },
  { section: 'queue', to: '/queue', label: 'Queue', icon: CloudUpload },
  { section: 'flagged', to: '/flagged', label: 'Flagged', icon: Flag },
];

/**
 * The four-destination shell every CA-02, CA-03, CA-07 and CA-08 screen
 * renders inside. CA-01 (sign in) renders outside it.
 *
 * Navigation moves from thumb to pointer as the viewport grows. Up to the
 * tablet breakpoint it is the bottom bar the wireframe draws
 * (`capture-app.html` `.pfoot`), reachable one-handed by an operator holding
 * the device. From 64rem it becomes a left rail: a bottom bar sits far from
 * both the pointer and the eye on a wide screen, and the width it was
 * defending is no longer scarce.
 *
 * Type size is constant across all three. A tablet in a plant room is held
 * at roughly the same distance as a phone, so growing the text with the
 * viewport would make it larger in the hand for no gain. The layout carries
 * the responsiveness on its own.
 */
export function AppShell() {
  const { queue, online } = useOfflineQueue();
  const { pathname } = useLocation();
  const active = activeSection(pathname);
  const { manifest } = usePartnerManifest();

  // `index.html` sets the build's icon before React mounts. Once the
  // tenant's manifest arrives, its own mark takes the tab (CAPTURE_JOBS.md
  // section 5), which is what a warm reload into a signed-in route needs.
  useEffect(() => {
    if (manifest) applyFavicon(manifest);
  }, [manifest]);

  const pendingCount = queue.filter(
    (item) => item.status === 'pending' || item.status === 'uploading',
  ).length;

  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* Desktop rail. Hidden while the bottom bar is the navigation. */}
      <nav
        aria-label="Sections"
        className="hidden shrink-0 border-r border-border px-3 py-4 lg:flex lg:w-56 lg:flex-col lg:gap-1"
      >
        <span className="mb-4 px-3 text-sm font-semibold">Smart Capture</span>
        {NAV_ITEMS.map(({ section, to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            aria-current={active === section ? 'page' : undefined}
            className={cn(
              'flex min-h-11 items-center gap-3 rounded-control px-3 text-sm text-muted transition-colors hover:text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              active === section && 'bg-white/5 text-brand-foreground',
            )}
          >
            <Icon className="size-5 shrink-0" />
            <span className="flex-1">{label}</span>
            {to === '/queue' && pendingCount > 0 && (
              <span className="flex size-5 items-center justify-center rounded-badge border border-brand text-[10px] text-brand">
                {pendingCount}
              </span>
            )}
          </Link>
        ))}
      </nav>

      {/*
        One measure for the content at every width. The phone column is
        unchanged at 28rem; the tablet widens to 42rem so two cards sit side
        by side; the desktop stops at 64rem so a line of text never runs past
        a comfortable reading length on a wide monitor.
      */}
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col sm:max-w-2xl lg:max-w-5xl">
        <header className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:justify-end">
          <span className="text-sm font-semibold lg:sr-only">Smart Capture</span>
          <Badge tone={online ? 'success' : 'warning'}>{online ? 'Online' : 'Offline'}</Badge>
        </header>

        <div className="px-4 sm:px-6">
          <DegradedNotice />
        </div>

        <main className="flex-1 px-4 py-3 sm:px-6 sm:py-5">
          <Outlet />
        </main>

        {/* Bottom bar. The rail replaces it from 64rem. */}
        <nav
          aria-label="Sections"
          className="sticky bottom-0 flex border-t border-border bg-surface lg:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {NAV_ITEMS.map(({ section, to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              aria-current={active === section ? 'page' : undefined}
              className={cn(
                'flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-xs text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                active === section && 'text-brand',
              )}
            >
              <span className="relative">
                <Icon className="size-5" />
                {to === '/queue' && pendingCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex size-4 items-center justify-center rounded-badge border border-brand text-[10px] text-brand">
                    {pendingCount}
                  </span>
                )}
              </span>
              {label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}
